import React from 'react';
import I18n from 'i18n-js';
import { ExecutionResult } from 'graphql';

import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedAddFavoriteSearch, buildClickedToastMessage } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { CoreFindFavorite, UndoDeleteMyFavorite, UpsertMyFavorite, core_apimessages_FavoriteType } from '../../schemas/rql';

import { useFindFavorite, FavoriteModal } from './find_favorite_context';
import { FindFavoriteProviderUpsertFavoriteModal } from './favorite_modal';
import FavoriteToast from '@reverbdotcom/commons/src/components/favorite_toast';

export function FindFavoriteLayout() {
  const { favorite, setFavorite, displayedModal, setDisplayedModal, isToastOpen, setIsToastOpen } = useFindFavorite();
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const url = useURL();

  const updateFavorite = function (key: 'feedEnabled' | 'emailEnabled', value: boolean) {
    const newFavorite = { ...favorite, [key]: value };

    if (key === 'feedEnabled' && !value) {
      newFavorite.emailEnabled = false;
    }

    setFavorite(newFavorite);
  };

  const handleUpsert = function (result: ExecutionResult<UpsertMyFavorite.Mutation>) {
    if (!result.errors.length) {
      const newFavorite: CoreFindFavorite.Favorite = {
        ...favorite,
        id: result.data.upsertMyFavorite?.favorite?.id,
        favorited: result.data.upsertMyFavorite?.favorite?.favorited,
      };
      trackEvent(buildClickedAddFavoriteSearch(newFavorite, url));
      setFavorite(newFavorite);
      setIsToastOpen(true);
    } else {
      setHasError(true);
      setErrorMessage(result.errors[0].message);
      setIsToastOpen(true);
    }
  };

  const handleToastClose = function (closeType: ToastCloseType) {
    setIsToastOpen(false);

    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(buildClickedToastMessage(url));
    }
  };

  // For some reason, after hitting "Undo" on the "Removed from Favorites" toast,
  // this component re-renders with isToastOpen: false, so the only way I could figure out
  // how to display the toast was by manually setting it here. I think it may be due to having to setIsToastOpen(false)
  // in #useFindFavoriteProvider, which was necessary to fix another bug.
  const handleUndoFavoriteRemove = (result: ExecutionResult<UndoDeleteMyFavorite.Mutation>) => {
    if (!result.errors.length) {
      setIsToastOpen(true);
      const newFavorite: CoreFindFavorite.Favorite = {
        ...favorite,
        favorited: result.data.undoDeleteMyFavorite?.favorite?.favorited,
      };
      setFavorite(newFavorite);
    } else {
      setHasError(true);
      setIsToastOpen(true);
    }
  };

  const modalTitle = I18n.t('discovery.favorites.confirmationModal.saveTitle');

  return (
    <>
      <FindFavoriteProviderUpsertFavoriteModal
        isOpen={displayedModal === FavoriteModal.Save}
        handleClose={() => { setDisplayedModal(FavoriteModal.None); }}
        handleUpdate={updateFavorite}
        modalTitle={modalTitle}
        searchTitle={favorite.title}
        feedEnabled={favorite.feedEnabled}
        emailEnabled={favorite.emailEnabled}
        queryParams={favorite.queryParams}
        handleMutationResult={handleUpsert}
        subtitle={favorite.subtitle}
      />
      <FavoriteToast
        favoriteId={favorite.id}
        isFavorited={favorite.favorited}
        favoriteType={core_apimessages_FavoriteType.SAVED_SEARCH}
        isOpen={isToastOpen}
        handleToastClose={handleToastClose}
        handleMutationResult={handleUndoFavoriteRemove}
        hasError={hasError}
        errorMessage={errorMessage}
        setHasError={setHasError}
      />
    </>
  );
}
